/* eslint-disable camelcase */

import {WithClientId} from '../editors/commons/clientIdModel';
import {
  CardGroupParam,
  CommentBodyParam,
  CustomFieldParam,
  FieldParam,
  FieldValueParam,
  ForwardParam,
  MoveTypeParam,
  RandomAssignParam,
  ReplyParam,
  SnoozeParam,
  TagParam,
  TeammateParam,
  TeammatesParam,
} from '../editors/commons/paramModel';
import {RuleActionDefinition} from './ruleParameterModel';

export enum RuleActionTypesEnum {
  ADD_COMMENT = 'add_comment',
  ADD_EXPRESSIONS_LOG_COMMENT = 'add_expressions_log_comment',
  AUTOLINK = 'autolink',
  ASSIGN = 'assign',
  RANDOM_ASSIGN = 'random_assign',
  ASSIGN_CONTACT_FIELD = 'assign_contact_field',
  UNASSIGN = 'unassign',
  MOVE = 'move',
  TAG = 'tag',
  TAG_PRIVATE = 'tag_private',
  UNTAG = 'untag',
  UNTAG_PRIVATE = 'untag_private',
  REPLY = 'reply',
  ARCHIVE = 'archive',
  UNARCHIVE = 'unarchive',
  SNOOZE = 'snooze',
  TRASH = 'trash',
  ADD_CONTACT_TO_GROUP = 'add_contact_to_group',
  REMOVE_CONTACT_FROM_GROUP = 'remove_contact_from_group',
  FORWARD = 'forward',
  NOTIFY_TEAMMATES = 'notify_teammates',
  NOTIFY_ASSIGNEE = 'notify_assignee',
  INCREMENT_CONVERSATION_METADATA = 'increment_conversation_metadata',
  DECREMENT_CONVERSATION_METADATA = 'decrement_conversation_metadata',
  SET_CONVERSATION_METADATA = 'set_conversation_metadata',
  CREATE_TOPICS = 'create_topics',
  ASSIGN_ACCOUNT_FIELD = 'assign_account_field',
  NOTIFY_ACCOUNT_FIELD = 'notify_account_field',
}

type Action<T extends RuleActionTypesEnum, P = {}> = {
  readonly integration_id: number;
  readonly type: T;
  readonly params: P;
};

// List of all condition types.

export type AssignAction = Action<RuleActionTypesEnum.ASSIGN, TeammateParam>;

export type RandomAssignAction = Action<RuleActionTypesEnum.RANDOM_ASSIGN, RandomAssignParam>;

export type AssignContactFieldAction = Action<RuleActionTypesEnum.ASSIGN_CONTACT_FIELD, CustomFieldParam>;

export type UnassignAction = Action<RuleActionTypesEnum.UNASSIGN>;

export type MoveAction = Action<RuleActionTypesEnum.MOVE, MoveTypeParam>;

export type TagAction = Action<RuleActionTypesEnum.TAG, TagParam>;
export type TagPrivateAction = Action<RuleActionTypesEnum.TAG_PRIVATE, TagParam>;

export type UntagAction = Action<RuleActionTypesEnum.UNTAG, TagParam>;
export type UntagPrivateAction = Action<RuleActionTypesEnum.UNTAG_PRIVATE, TagParam>;

export type ReplyAction = Action<RuleActionTypesEnum.REPLY, ReplyParam>;

export type ArchiveAction = Action<RuleActionTypesEnum.ARCHIVE>;

export type UnarchiveAction = Action<RuleActionTypesEnum.UNARCHIVE>;

export type SnoozeAction = Action<RuleActionTypesEnum.SNOOZE, SnoozeParam>;

export type TrashAction = Action<RuleActionTypesEnum.TRASH>;

export type AddContactToGroupAction = Action<RuleActionTypesEnum.ADD_CONTACT_TO_GROUP, CardGroupParam>;

export type RemoveContactFromGroupAction = Action<
  RuleActionTypesEnum.REMOVE_CONTACT_FROM_GROUP,
  CardGroupParam
>;

export type ForwardAction = Action<RuleActionTypesEnum.FORWARD, ForwardParam>;

export type NotifyTeammatesAction = Action<RuleActionTypesEnum.NOTIFY_TEAMMATES, TeammatesParam>;

export type NotifyAssignee = Action<RuleActionTypesEnum.NOTIFY_ASSIGNEE>;

export type IncrementMetadataAction = Action<RuleActionTypesEnum.INCREMENT_CONVERSATION_METADATA, FieldParam>;

export type SetMetadataAction = Action<RuleActionTypesEnum.SET_CONVERSATION_METADATA, FieldValueParam>;

export type AssignAccountFieldAction = Action<RuleActionTypesEnum.ASSIGN_ACCOUNT_FIELD, CustomFieldParam>;

export type NotifyAccountField = Action<RuleActionTypesEnum.NOTIFY_ACCOUNT_FIELD, CustomFieldParam>;

export type AddCommentAction = Action<RuleActionTypesEnum.ADD_COMMENT, CommentBodyParam>;

export type AddExpressionsLogCommentAction = Action<RuleActionTypesEnum.ADD_EXPRESSIONS_LOG_COMMENT>;

export type AutolinkAction = Action<RuleActionTypesEnum.AUTOLINK>;

export type RuleAction = (
  | AssignAction
  | RandomAssignAction
  | AssignContactFieldAction
  | UnassignAction
  | MoveAction
  | TagAction
  | TagPrivateAction
  | UntagAction
  | UntagPrivateAction
  | ReplyAction
  | ArchiveAction
  | UnarchiveAction
  | SnoozeAction
  | TrashAction
  | AddContactToGroupAction
  | RemoveContactFromGroupAction
  | ForwardAction
  | NotifyTeammatesAction
  | NotifyAssignee
  | IncrementMetadataAction
  | SetMetadataAction
  | AssignAccountFieldAction
  | NotifyAccountField
  | AddCommentAction
  | AddExpressionsLogCommentAction
  | AutolinkAction
) & {$isReadOnly?: boolean};

export type ClientRuleAction = WithClientId<RuleAction>;
export interface RuleActionGroup {
  id: RuleEditorActionsGroupsEnum;
  items: ReadonlyArray<RuleActionTypesEnum>;
  disabled?: ReadonlyArray<RuleActionTypesEnum>;
}

export interface ThirdPartyRuleActionGroup {
  id: RuleEditorActionsGroupsEnum;
  items: ReadonlyArray<RuleActionDefinition>;
}

export enum RuleEditorActionsGroupsEnum {
  TEAMMATES = 'teammates',
  STATUS = 'status',
  ORGANIZATION = 'organization',
  MESSAGE = 'message',
  COMMENT = 'comment',
  CONTACTS = 'contacts',
  THIRD_PARTY = 'third-party',
  ACCOUNT = 'account',
}

export const ruleActionsGroups: ReadonlyArray<RuleActionGroup> = [
  {
    id: RuleEditorActionsGroupsEnum.TEAMMATES,
    items: [
      RuleActionTypesEnum.RANDOM_ASSIGN,
      RuleActionTypesEnum.UNASSIGN,
      RuleActionTypesEnum.NOTIFY_TEAMMATES,
      RuleActionTypesEnum.NOTIFY_ASSIGNEE,
    ],
  },
  {
    id: RuleEditorActionsGroupsEnum.STATUS,
    items: [
      RuleActionTypesEnum.ARCHIVE,
      RuleActionTypesEnum.UNARCHIVE,
      RuleActionTypesEnum.TRASH,
      RuleActionTypesEnum.SNOOZE,
    ],
  },
  {
    id: RuleEditorActionsGroupsEnum.ORGANIZATION,
    items: [RuleActionTypesEnum.TAG, RuleActionTypesEnum.UNTAG, RuleActionTypesEnum.MOVE],
  },
  {
    id: RuleEditorActionsGroupsEnum.MESSAGE,
    items: [RuleActionTypesEnum.REPLY, RuleActionTypesEnum.FORWARD],
  },
  {
    id: RuleEditorActionsGroupsEnum.COMMENT,
    items: [RuleActionTypesEnum.ADD_COMMENT],
  },
  {
    id: RuleEditorActionsGroupsEnum.CONTACTS,
    items: [
      RuleActionTypesEnum.ADD_CONTACT_TO_GROUP,
      RuleActionTypesEnum.ASSIGN_CONTACT_FIELD,
      RuleActionTypesEnum.REMOVE_CONTACT_FROM_GROUP,
    ],
  },
] as const;

export function thirdPartyGroup(actions: ReadonlyArray<RuleActionDefinition>): ThirdPartyRuleActionGroup {
  return {
    id: RuleEditorActionsGroupsEnum.THIRD_PARTY,
    items: actions,
  };
}

export const ruleExpressionsActionsGroups: ReadonlyArray<RuleActionGroup> = [
  {
    id: RuleEditorActionsGroupsEnum.ORGANIZATION,
    items: [RuleActionTypesEnum.CREATE_TOPICS, RuleActionTypesEnum.AUTOLINK],
  },
  {
    id: RuleEditorActionsGroupsEnum.COMMENT,
    items: [RuleActionTypesEnum.ADD_EXPRESSIONS_LOG_COMMENT],
  },
];
export const ruleActionsAccountGroups: ReadonlyArray<RuleActionGroup> = [
  {
    id: RuleEditorActionsGroupsEnum.ACCOUNT,
    items: [RuleActionTypesEnum.ASSIGN_ACCOUNT_FIELD, RuleActionTypesEnum.NOTIFY_ACCOUNT_FIELD],
  },
];
